/**
 * Order creation logic.
 * Replaces: mixins/order/create.js
 */
import { useNuxtApp } from '#app'

export function useOrderCreate() {
  const { $api, $bus, $notification, $panel } = useNuxtApp()

  const createOrder = async ({ items, customer, notes }: {
    items: any[]
    customer: any
    notes?: string
  }) => {
    try {
      // Old Nuxt 2 `API.Order.create` accepted `{ customer, items, notes }`
      // raw and did the payload transformation internally. In the Nuxt 4
      // rewrite, `$api.order.create` expects the already-transformed shape
      // (`customerId` + `items: { type, id }[]`). This composable is the
      // bridge — extracts the customer id and maps each item to its `{ type,
      // id }` tuple. Without this the request went out as
      // `{ idCustomer: undefined, items: [raw model instance] }` → 500.
      // See .docs/performance.md / commit notes.
      const customerId: string | undefined =
        customer?.idCustomer?.toString?.()
          ?? customer?.objectID?.()
          ?? undefined

      const mappedItems = items.map((item) => ({
        type: (item?.constructor as any)?.modelName?.() ?? '',
        id: item?.objectID?.() ?? '',
      }))

      if (!customerId) {
        $notification.error({ title: 'Missing customer', text: 'Cannot create a sale without a customer.' })
        return null
      }

      const response = await $api.order.create({
        customerId,
        notes: notes ?? '',
        items: mappedItems,
      })

      const order = response?.data

      if (order) {
        for (const item of items) {
          const modelName = (item?.constructor as any)?.modelName?.()
          if (modelName) {
            const name = modelName.charAt(0).toUpperCase() + modelName.slice(1)
            $bus.emit.updated(name, item)
          }
        }

        $notification.toast({
          title: 'Created sale',
          text: 'Successfully created a new sale',
          variant: 'success',
        })

        $panel.create(`/order/${order.idOrder}`)
      }

      return order
    } catch (e: any) {
      console.error(e)
      $notification.error({ errors: e?.errors })
      return null
    }
  }

  return { createOrder }
}
